import {
  APP_ALL_OFFERS_ROUTE,
  APP_CATEGORY_ROUTE,
  APP_LANDING_PAGE_ROUTE,
  Direction,
  DirectionType,
  EXPERIMENT_VARIATIONS,
  NUMBER_0,
  NUMBER_16,
  NUMBER_300,
  NUMBER_4,
  NUMBER_8,
} from '~shared/constants';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewRef,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, filter, startWith, takeUntil } from 'rxjs/operators';
import get from 'lodash.get';
import { MmPopoverRef, MmPopoverService, PreferablePositions } from '~shared/modules/popover/popover.module';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { Category } from '~app/modules/generic/models';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { CategoriesNavbarApplicationEventHandler } from '~app/modules/generic/helpers';
import { CachedCategoriesUtil } from '~app/modules/generic/utils/cached-categories.util';
import { NavigationEnd, Router } from '@angular/router';
import { TopCategoriesNavbarItem } from '~app/modules/generic/models/top-categories-navbar-item.interface';
import { APP_BASE_HREF, DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as fromStoryblok from '~app/modules/storyblok/store';
import { BuyerState } from '~app/modules/generic/store';
import { select, Store } from '@ngrx/store';
import { StoryData } from 'storyblok-js-client';
import { TopMenuPromotionLinkBlok } from '~app/modules/storyblok/models/storyblok-global-content.model';
import { PROMO_CATEGORIES_ID, TOP_MENU_PROMOTION_LINK_ID } from '~app/modules/generic/constants/promo-constants';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { CategoryNavigationService } from '~app/modules/generic/services/category-navigation.service';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { ApplicationEventType, CarouselActionType } from '~shared/modules/application-event/constants';
import { GATrackingEventType } from '~shared/modules/ga-tracking/constants';

@Component({
  selector: 'app-top-categories-navbar',
  templateUrl: './top-categories-navbar.component.html',
  styleUrls: ['./top-categories-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopCategoriesNavbarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() categories: Category[];

  @ViewChild('nestedCategoriesRef') nestedCategoryRef: TemplateRef<any>;
  @ViewChild('categoryNavBar', { static: false }) categoryNavBar: ElementRef;

  private destroySubject$: Subject<void> = new Subject();
  private SYMBOL_WIDTH: number = NUMBER_8;
  private SLIDE_GUTTER: number = NUMBER_16;
  private applicationEventHandler: CategoriesNavbarApplicationEventHandler;
  private lastScrollLeft: number = NUMBER_0;
  private horizontalScrollEvent$: Subject<Event> = new Subject<Event>();
  private timeOutDelay;
  private isOnCategoryBar: boolean = false;
  protected readonly NUMBER_4: number = NUMBER_4;
  // @TODO: Remove Optimizely logic whenever is not needed (MACO-1485)
  private shouldShowSidenavBasedOnVariation: boolean = false;

  categoryRoute: string = `/${APP_CATEGORY_ROUTE}`;
  selectedCategory: Category;
  nestedCategoryPopoverRef: MmPopoverRef;
  promoCategoryId: string = PROMO_CATEGORIES_ID;
  isNavigationActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  categoryClickGALabel: string = 'HEADER.NESTED_CATEGORIES.SHOW';
  showContentWrapper: boolean = true;
  overlayTopSpace: number;
  categoriesLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  staticItemsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  topOffersLink: TopCategoriesNavbarItem = { category: null, width: 0 };
  topMenuPromotionLink: TopCategoriesNavbarItem = { category: null, width: 0, storyBlokEditable: '' };
  buyerCategories: Array<TopCategoriesNavbarItem> = [];

  get isSidenavMenuVisible(): boolean {
    return this.shouldShowSidenavBasedOnVariation;
  }

  get isPromotionsMenuSelected() {
    return this.selectedCategory && this.selectedCategory.id === PROMO_CATEGORIES_ID;
  }

  constructor(
    private popoverService: MmPopoverService,
    private elementRef: ElementRef,
    private changeDetector: ChangeDetectorRef,
    private categoriesService: CategoriesService,
    private translateService: TranslateService,
    private store: Store<BuyerState>,
    applicationEventService: ApplicationEventService,
    private router: Router,
    private categoryNavigationService: CategoryNavigationService,
    private gtmDatalayerService: GtmDatalayerService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {
    this.applicationEventHandler = new CategoriesNavbarApplicationEventHandler(
      applicationEventService,
      this.translateService
    );

    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          filter<NavigationEnd>((event: any) => event instanceof NavigationEnd),
          startWith(this.router)
        )
        .subscribe((event) => {
          this.showContentWrapper = !UrlUtils.getIsEmployeeRestrictedView(event.url);
        });
    }

    this.horizontalScrollEvent$.pipe(debounceTime(100)).subscribe((event) => this.handleHorizontalScroll(event));
  }

  @HostListener('window:mouseover', ['$event.target'])
  private onGlobalMouseOver(targetElement): void {
    if (!this.nestedCategoryPopoverRef) {
      return;
    }

    const isCategoryOver = this.elementRef.nativeElement.contains(targetElement);
    const overlay = get(this.nestedCategoryPopoverRef, 'overlay.overlayElement');
    const isOverNested = overlay && overlay.contains(targetElement);
    const shouldCloseNestedCategoriesList = !isCategoryOver && !isOverNested;

    if (shouldCloseNestedCategoriesList) {
      setTimeout(() => {
        this.closeNestedCategoriesPopover();
      }, NUMBER_300);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['categories'] && !!this.categories?.length) {
      this.initializeCategories();
    }
  }

  ngOnInit() {
    this.loadSidenavMenuEnablers();
  }

  ngAfterViewInit(): void {
    this.isNavigationActive$.subscribe((isActive: boolean) =>
      this.document.getElementsByTagName('html')[0].classList.toggle('category-navigation-noScroll', isActive)
    );

    this.overlayTopSpace =
      this.document.getElementById('BUYER_PAGES.HEADER')?.offsetHeight +
      this.document.getElementById('BUYER_PAGES.CATEGORY_NAVIGATION')?.offsetHeight;
  }

  ngOnDestroy() {
    this.closeNestedCategoriesPopover();
    this.applicationEventHandler.destroy();
    this.destroySubject$.next();
    this.destroySubject$.complete();
    clearTimeout(this.timeOutDelay);
  }

  private triggerScrollEvent(direction: DirectionType): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_HORIZONTAL_SCROLL,
      category: ApplicationEventType.Navigation,
      action: CarouselActionType.HorizontalScroll,
      label: direction,
    });
  }

  private handleHorizontalScroll(event: Event) {
    const element = event.target as HTMLElement;
    const currentScrollLeft = element.scrollLeft;

    if (currentScrollLeft > this.lastScrollLeft) {
      this.triggerScrollEvent(Direction.RIGHT);
    } else if (currentScrollLeft < this.lastScrollLeft) {
      this.triggerScrollEvent(Direction.LEFT);
    }

    this.lastScrollLeft = currentScrollLeft;
  }

  private loadSidenavMenuEnablers(): void {
    this.categoryNavigationService
      .isSidenavMenuEnabled()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((variation) => {
        this.shouldShowSidenavBasedOnVariation = !variation || variation === EXPERIMENT_VARIATIONS.VARIANT_TWO;
      });
  }

  private openPopOverNavigationWithDelay(origin?: HTMLSpanElement) {
    if (this.isOnCategoryBar) {
      this.openPopOverNavigation(origin);
    } else {
      this.timeOutDelay = setTimeout(() => {
        this.openPopOverNavigation(origin);
      }, NUMBER_300);
      this.isOnCategoryBar = true;
    }
  }

  private openPopOverNavigation(origin: HTMLSpanElement) {
    this.openPopOverByDevice(origin);
    this.isNavigationActive$.next(true);
    this.onAfterPopOverClosedSubscription();
  }

  private mapCategoriesInOneDimension(newSelectedCategory: Category) {
    const newMappedChildren = [];
    const newMappedCategory = { ...newSelectedCategory };
    if (newSelectedCategory.id !== PROMO_CATEGORIES_ID) {
      for (const child of newSelectedCategory.children) {
        newMappedChildren.push([child]);
      }
      newMappedCategory.children = newMappedChildren;
    }
    newSelectedCategory = newMappedCategory;
    return newSelectedCategory;
  }

  private loadStaticContent(): void {
    if (CachedCategoriesUtil.isCachedPromotionCategoriesExisted()) {
      combineLatest([
        this.store.pipe(select(fromStoryblok.getNavigation)),
        this.translateService.get('HOME.HOME_PAGE.HEADER.CATEGORIES.ALL_OFFERS'),
      ])
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(([navigationStory, translation]) => {
          this.initializePromoLinkItem(navigationStory);
          this.initializeTopOffersItem(translation, CachedCategoriesUtil.getCachedPromotionCategories());
          this.staticItemsLoading$.next(false);
        });

      return;
    }

    combineLatest([
      this.categoriesService.getCategories(),
      this.store.pipe(select(fromStoryblok.getNavigation)),
      this.translateService.get('HOME.HOME_PAGE.HEADER.CATEGORIES.ALL_OFFERS'),
    ])
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(([promoCategories, navigationStory, translation]) => {
        this.initializePromoLinkItem(navigationStory);
        this.initializeTopOffersItem(translation, promoCategories);
        this.staticItemsLoading$.next(false);
      });
  }

  // START - TOP MENU PROMOTION LINK _________________________
  private initializePromoLinkItem(navigationStory: StoryData): void {
    const topMenuPromotionLink: TopMenuPromotionLinkBlok = navigationStory?.content?.global?.[0];
    if (topMenuPromotionLink) {
      this.topMenuPromotionLink = {
        category: {
          id: TOP_MENU_PROMOTION_LINK_ID,
          children: [],
          parents: [],
          slug: topMenuPromotionLink.link,
          name: topMenuPromotionLink.title,
        },
        width: topMenuPromotionLink.title.length * this.SYMBOL_WIDTH + this.SLIDE_GUTTER,
        storyBlokEditable: topMenuPromotionLink._editable,
      };
    }
  }
  // END - TOP MENU PROMOTION LINK _________________________

  // START - PROMOTION CATEGORIES __________________________
  private initializeTopOffersItem(translation: string, promoCategories: Array<Category>): void {
    this.topOffersLink = {
      category: {
        id: PROMO_CATEGORIES_ID,
        children: promoCategories,
        parents: [],
        slug: null,
        name: translation,
      },
      width: translation.length * this.SYMBOL_WIDTH + this.SLIDE_GUTTER,
    };
  }
  // END - PROMOTION CATEGORIES __________________________

  // START - BUYER CATEGORIES ____________________________
  private initBuyerCategories() {
    this.buyerCategories = this.categories.map((category: Category) => {
      const categoryWidth = category.name.length * this.SYMBOL_WIDTH + this.SLIDE_GUTTER;
      return { category, width: categoryWidth };
    });

    this.categoriesLoading$.next(false);
  }
  // END - BUYER CATEGORIES ____________________________

  private initializeCategories(): void {
    this.initBuyerCategories();
    this.loadStaticContent();
  }

  private openPopOverByDevice(origin?: HTMLSpanElement) {
    this.nestedCategoryPopoverRef = this.popoverService.open({
      origin,
      content: this.nestedCategoryRef,
      hasBackdrop: false,
      preferedPosition: PreferablePositions.bottomLeft,
    });
  }

  private onAfterPopOverClosedSubscription() {
    this.nestedCategoryPopoverRef.afterClosed$.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.applicationEventHandler.onMenuClosed();
      this.selectedCategory = null;
      this.nestedCategoryPopoverRef = null;
      setTimeout(() => {
        if (this.changeDetector && !(this.changeDetector as ViewRef).destroyed) {
          this.changeDetector.detectChanges();
        }
      });
    });
  }

  private onShowAllPromotions(selectedCategoryName, label?: string): void {
    this.applicationEventHandler.publishAppNavigation(selectedCategoryName, label);
    this.closeNestedCategoriesPopover();
    this.applicationEventHandler.publishSeeAllPromotion();
  }

  onHorizontalScroll(event: Event): void {
    this.horizontalScrollEvent$.next(event);
  }

  trackById(_, displayCategory: TopCategoriesNavbarItem) {
    return displayCategory?.category?.id;
  }

  onCategoryLeave() {
    clearTimeout(this.timeOutDelay);
  }

  onCategoryBarLeave() {
    this.isOnCategoryBar = false;
  }

  onCategorySelect(newSelectedCategory: Category, origin?: HTMLSpanElement) {
    // happens when page is rendering and user already hovered on categories carousel
    if (newSelectedCategory === null || newSelectedCategory?.id === TOP_MENU_PROMOTION_LINK_ID) {
      if (this.nestedCategoryPopoverRef) {
        this.closeNestedCategoriesPopover();
      }
      return;
    }

    if (this.nestedCategoryPopoverRef) {
      const currentSelectedCategoryId = get(this.selectedCategory, 'id');

      if (currentSelectedCategoryId && currentSelectedCategoryId === newSelectedCategory.id) {
        this.closeNestedCategoriesPopover();
        return;
      }
      this.closeNestedCategoriesPopover();
    }

    this.selectedCategory = this.mapCategoriesInOneDimension(newSelectedCategory);

    // this.openPopOverNavigationWithDelay(origin as unknown as HTMLSpanElement);
    this.openPopOverNavigationWithDelay(origin as unknown as HTMLSpanElement);

    if (this.isPromotionsMenuSelected) {
      this.applicationEventHandler.publishPromotionsMenuOpen();
    }
  }

  closeNestedCategoriesPopover() {
    if (this.nestedCategoryPopoverRef) {
      this.isNavigationActive$.next(false);
      this.nestedCategoryPopoverRef.data = null;
      this.nestedCategoryPopoverRef.close();
    }
  }

  closeCategoryNavigation() {
    setTimeout(() => {
      this.closeNestedCategoriesPopover();
    }, NUMBER_300);
  }

  onRootCategoryClick(category: Category) {
    if (category.id === PROMO_CATEGORIES_ID) {
      this.onShowAllPromotions(category.name, this.categoryClickGALabel);
      return;
    }
    this.onNestedCategoryClick(category.name, this.categoryClickGALabel);
  }

  navigateTo(category: Category): void {
    if (isPlatformBrowser(this.platformId)) {
      const url: string = this.getNavigationLink(category);
      window.location.href = url;
    }
  }

  getNavigationLink(category: Category): string {
    const baseUrl = `/${this.baseHref.replace(new RegExp('/', 'g'), '')}`;
    if (category.id === PROMO_CATEGORIES_ID) {
      return `${baseUrl}/${APP_LANDING_PAGE_ROUTE}/${APP_ALL_OFFERS_ROUTE}`;
    }
    if (category.id === TOP_MENU_PROMOTION_LINK_ID) {
      return category.slug.startsWith('/') ? `${baseUrl}${category.slug}` : `${baseUrl}/${category.slug}`;
    }
    return `${baseUrl}${this.categoryRoute}/${category.slug}`;
  }

  onNestedCategoryClick(nestedCategoryName: string, label?: string, parentCategoryName?: string): void {
    const title = parentCategoryName ? `${parentCategoryName}/${nestedCategoryName}` : nestedCategoryName;
    this.applicationEventHandler.publishAppNavigation(title, label);
    this.closeNestedCategoriesPopover();
  }
}
